import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Eye, Building2 } from 'lucide-react'
import { Card, CardContent } from './ui/Card'
import { Button } from './ui/Button'
import { getImageUrl } from '../lib/catalogUrl'

export type CatalogShowcaseEntry = {
  id: number
  name: string
  slug: string
  logoUrl?: string | null
  description?: string | null
  viewCount?: number
  catalogUrl?: string
  plan?: string
  badge?: string | null
}

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
}

type Props = {
  catalogs: CatalogShowcaseEntry[]
  isLoading?: boolean
  showViewAll?: boolean
}

export function CatalogShowcaseGrid({ catalogs, isLoading, showViewAll = true }: Props) {
  if (isLoading) {
    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="h-36 bg-gray-100 rounded-xl animate-pulse" />
        ))}
      </div>
    )
  }

  if (catalogs.length === 0) {
    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="border-dashed border-2 border-gray-200">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-gray-100 mx-auto mb-3 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-gray-500 text-sm">Contoh katalog akan segera tersedia</p>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <>
      <motion.div
        className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        {catalogs.map((catalog) => (
          <motion.div key={catalog.slug} variants={fadeInUp}>
            <Card className="h-full hover:shadow-lg transition-shadow group">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center shrink-0">
                    {catalog.logoUrl ? (
                      <img
                        src={getImageUrl(catalog.logoUrl) || ''}
                        alt={catalog.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-white font-bold text-lg w-full h-full flex items-center justify-center bg-indigo-500">
                        {catalog.name.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    {catalog.badge && (
                      <span
                        className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded mb-1 ${
                          catalog.badge === 'Premium'
                            ? 'bg-amber-500 text-white'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {catalog.badge}
                      </span>
                    )}
                    <h3 className="font-semibold text-gray-900 truncate group-hover:text-indigo-600 transition-colors">
                      {catalog.name}
                    </h3>
                    {catalog.description && (
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{catalog.description}</p>
                    )}
                    {catalog.viewCount != null && catalog.viewCount > 0 && (
                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                        <Eye className="w-3 h-3" />
                        <span>{catalog.viewCount.toLocaleString()} views</span>
                      </div>
                    )}
                    <a
                      href={catalog.catalogUrl || `/catalog/${catalog.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800"
                    >
                      Lihat Katalog <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {showViewAll && (
        <div className="text-center mt-8">
          <Link to="/catalog">
            <Button variant="outline" size="lg">
              Lihat Semua Katalog
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      )}
    </>
  )
}
